/* React Higher Order Component which fetches activity edit access data for activity where activityId = props.activityId.
    ...Edit access data will refresh via based on expiration date and pass changes via props
    Child (InnerComponent) will only render once activity data is successfully fetched.
    child will receive props: {
        activityEditAccessInfo: PropTypes.object
    }

*/

import React from 'react';
import PropTypes from 'prop-types';
const Moment = require('moment');

const ActivityActions = require('../../actions/ActivityActions');
const ActivityStore = require('../../stores/activity_store_reflux');
const PermissionsUtil = require('../../../../app/shared/PermissionsUtil');
const ReactUtil = require('../../util/ReactUtil');
import ErrorBox from '../common/ErrorBox';

const propTypes = {
    activityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    channelStoreState: PropTypes.shape({ channel: PropTypes.shape({ user_permissions: PropTypes.object.isRequired }) }).isRequired,
    projectStoreState: PropTypes.shape({ project: PropTypes.shape({ user_permissions: PropTypes.object.isRequired }) }).isRequired,
    documentVisible: PropTypes.bool.isRequired,
};

const defaultProps = {
};

const MAX_TIMEOUT = 2147483647; // https://stackoverflow.com/questions/3468607/why-does-settimeout-break-for-large-millisecond-delay-values

export default function withActivityEditAccessData(WrappedComponent) {
    class WithActivityEditAccessData extends React.Component {
        constructor(props) {
            super(props);

            this.state = {
                activityEditAccessInfo: ActivityStore.getStateActivity(props.activityId, props.projectStoreState.project.id, props.channelStoreState.channel.id).activityEditAccessInfo,
                fatalError: null
            }

            this.refreshEditAccess = this.refreshEditAccess.bind(this);
            this.scheduleNextRefresh = this.scheduleNextRefresh.bind(this);
            this.unsubscribes = [ActivityStore.listen(this.onActivityStoreChange.bind(this))];
        }

        UNSAFE_componentWillMount() {
            if (
                !this.state.activityEditAccessInfo
                || Moment(this.state.activityEditAccessInfo.access_key_expires_at).valueOf() <= Moment().valueOf()
            ) {
                this.refreshEditAccess();
            }
        }

        componentWillUnmount() {
            if (this.updateInfoTimeoutId) window.clearTimeout(this.updateInfoTimeoutId);
            this.unsubscribes.forEach(unsubscribeCallback => unsubscribeCallback());
        }

        onActivityStoreChange(newState) {
            if (!newState.action || newState.action.id !== 'activity-edit-access-refresh') {
                return;
            }

            const self = this;
            let state = {};
            let setNewPoll;
            let msToExpiration;

            if (newState.action.error) {
                setNewPoll = false;
                state = { ...state, fatalError: newState.action.error };
            }
            else {
                setNewPoll = true;
                const activityEditAccessInfo = ActivityStore.getStateActivity(this.props.activityId, this.props.projectStoreState.project.id, this.props.channelStoreState.channel.id).activityEditAccessInfo;
                msToExpiration = Moment(activityEditAccessInfo.access_key_expires_at).valueOf() - Moment().valueOf();
                state = { ...state, activityEditAccessInfo: activityEditAccessInfo };
            }

            this.setState(state, () => {
                if (!setNewPoll) return;
                self.scheduleNextRefresh(msToExpiration > 0 ? msToExpiration : 0);
            });
        }

        scheduleNextRefresh(timeoutMs) {
            const self = this;
            const clampedMs = timeoutMs <= MAX_TIMEOUT ? timeoutMs : MAX_TIMEOUT;
            if (this.updateInfoTimeoutId) window.clearTimeout(this.updateInfoTimeoutId);
            this.updateInfoTimeoutId = window.setTimeout(self.refreshEditAccess, clampedMs);
        }

        refreshEditAccess() {
            if (!this.props.documentVisible) {
                this.scheduleNextRefresh(10000); // delay call while window is not visible
                return;
            }

            const project = this.props.projectStoreState.project;
            const channel = this.props.channelStoreState.channel;
            if (
                !project 
                || !channel 
                || (
                    !PermissionsUtil.canEditActivity(channel.user_permissions, project.user_permissions)
                    && !PermissionsUtil.canReviewActivity(channel.user_permissions, project.user_permissions)
                    && !PermissionsUtil.canPreviewActivity(channel.user_permissions, project.user_permissions)
                )
            ) {
                return;
            }

            ActivityActions.getActivityEditAccessInfo('activity-edit-access-refresh', { activityId: this.props.activityId });
        }

        render() {
            if (this.state.fatalError) {
                return <ErrorBox error={this.state.fatalError} allowStackTrace={false}/>;
            }

            if (!this.state.activityEditAccessInfo) {
                return null;
            }

            return (
                <WrappedComponent
                {...this.props}
                activityEditAccessInfo={this.state.activityEditAccessInfo}
                />
            );
        }
    }

    WithActivityEditAccessData.propTypes = propTypes;
    WithActivityEditAccessData.defaultProps = defaultProps;
    WithActivityEditAccessData.displayName = `WithActivityEditAccessData(${ReactUtil.getComponentDisplayName(WrappedComponent)})`
    return WithActivityEditAccessData;
}
